import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import {
  Activity, RefreshCw, Loader2, AlertTriangle, ThumbsUp, ThumbsDown,
  Eye, ShieldCheck, Send, Link2, Clock, ExternalLink, ChevronDown, ChevronRight,
  MailOpen,
} from 'lucide-react';

// Client Activity — what happened after we sent the link.
//
// Sending is recorded when the mail goes out. Everything after it lands here:
// the click onto the shortlist, the mobile number that verified, every profile
// opened, and every yes or no with the number that gave it.
//
// The mail carries a tracking pixel, so mail opens appear here too — but they
// are shown for what they are. A pixel load proves an image was fetched, not
// that a person read anything: Apple Mail Privacy Protection and most corporate
// scanners fetch every image the moment the mail is delivered. Those loads are
// separated out as "on delivery" rather than counted as reads, and a client who
// blocks images and reads the mail properly produces nothing at all. The click
// onto the shortlist remains the only signal that cannot be faked by a machine.

const KIND_LABEL = {
  email_opened: 'Mail images loaded',
  shortlist_opened: 'Opened the shortlist',
  gate_shown: 'Saw the verification wall',
  otp_requested: 'Asked for a code',
  otp_failed: 'Code could not be sent',
  otp_verified: 'Verified their number',
  profile_opened: 'Opened a profile',
  decision: 'Gave a decision',
};

const KIND_STYLE = {
  email_opened: 'bg-teal-100 text-teal-700',
  shortlist_opened: 'bg-sky-100 text-sky-700',
  gate_shown: 'bg-slate-100 text-slate-600',
  otp_requested: 'bg-amber-100 text-amber-700',
  otp_failed: 'bg-red-100 text-red-700',
  otp_verified: 'bg-emerald-100 text-emerald-700',
  profile_opened: 'bg-indigo-100 text-indigo-700',
  decision: 'bg-purple-100 text-purple-700',
};

const when = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return '—';
  return d.toLocaleString('en-IN', {
    day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', hour12: true,
  });
};

const ago = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d)) return null;
  const mins = Math.floor((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

const mask = (m) => (m ? `+91 ${String(m).slice(-4).padStart(10, 'X')}` : null);

const Stat = ({ label, value, accent }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-4">
    <p className="text-xs text-slate-500 uppercase tracking-wide">{label}</p>
    <p className={`text-2xl font-bold mt-1 ${accent || 'text-slate-900'}`}>{value}</p>
  </div>
);

const DecisionChip = ({ decision }) => {
  if (decision === 'interested') {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1
                       rounded-full bg-emerald-100 text-emerald-700">
        <ThumbsUp className="w-3 h-3" /> Wants to meet
      </span>
    );
  }
  if (decision === 'not_now') {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1
                       rounded-full bg-slate-200 text-slate-600">
        <ThumbsDown className="w-3 h-3" /> Not right
      </span>
    );
  }
  return <span className="text-xs text-slate-400">No answer yet</span>;
};

export default function ClientActivityPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [open, setOpen] = useState({});

  const load = useCallback(async () => {
    try {
      const r = await api.get('/shortlist/activity');
      setRows(r.data?.shortlists || []);
      setError('');
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not load client activity.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const totals = rows.reduce((a, s) => ({
    sent: a.sent + (s.sent ? 1 : 0),
    opened: a.opened + (s.shortlist_opens > 0 ? 1 : 0),
    verified: a.verified + (s.verified_numbers?.length ? 1 : 0),
    decisions: a.decisions + (s.decisions || 0),
    mail: a.mail + (s.email?.confirmed ? 1 : 0),
  }), { sent: 0, opened: 0, verified: 0, decisions: 0, mail: 0 });

  if (loading) {
    return (
      <div className="page-container flex items-center justify-center py-24 text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading client activity…
      </div>
    );
  }

  return (
    <div className="page-container space-y-5 pb-16 max-md:px-4 max-md:pt-4 max-md:pb-[calc(4rem+env(safe-area-inset-bottom))]">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Activity className="w-6 h-6" /> Client activity
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            What happened after we sent the link.
          </p>
        </div>
        <button onClick={() => { setLoading(true); load(); }}
          className="inline-flex items-center gap-2 px-3 min-h-[44px] rounded-lg
                     border border-slate-200 bg-white text-sm text-slate-700">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {error ? (
        <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700
                        rounded-lg px-3 py-2 text-sm">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> <span>{error}</span>
        </div>
      ) : null}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Stat label="Shortlists sent" value={totals.sent} />
        <Stat label="Mail opened" value={totals.mail} accent="text-teal-700" />
        <Stat label="Links clicked" value={totals.opened} accent="text-sky-700" />
        <Stat label="Numbers verified" value={totals.verified} accent="text-emerald-700" />
        <Stat label="Decisions given" value={totals.decisions} accent="text-purple-700" />
      </div>

      <div className="flex items-start gap-2 bg-slate-50 border border-slate-200
                      text-slate-600 rounded-lg px-3 py-2 text-xs leading-relaxed">
        <Clock className="w-4 h-4 mt-0.5 shrink-0" />
        <span>
          <b>Mail opened</b> means an image in the mail was fetched — not that
          anyone read it. Apple Mail and most corporate scanners fetch every image
          the moment mail is delivered; those are shown separately as
          <em> on delivery</em>. A client who blocks images and reads the mail
          properly shows nothing at all. <b>Links clicked</b> is the first signal
          a machine cannot produce on its own.
        </span>
      </div>

      {rows.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-8 text-center
                        text-slate-500 text-sm">
          No shortlists have been sent yet. When one goes out, everything the
          client does with it shows up here.
        </div>
      ) : null}

      <div className="space-y-3">
        {rows.map((s) => {
          const isOpen = !!open[s.shortlist_id];
          const quiet = s.shortlist_opens === 0;
          return (
            <div key={s.shortlist_id}
                 className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="p-4 space-y-3">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-900">
                      {s.title || 'Shortlist'}
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5 max-md:break-words">
                      {s.sent?.company ? `${s.sent.company} · ` : ''}
                      {s.sent?.to || 'not sent by the engine'}
                      {s.revoked ? ' · withdrawn' : ''}
                    </p>
                  </div>
                  <a href={s.url} target="_blank" rel="noreferrer"
                     className="inline-flex items-center gap-1.5 text-xs text-slate-600
                                border border-slate-200 rounded-lg px-2.5 py-2 shrink-0
                                max-md:w-full max-md:justify-center max-md:min-h-[44px]">
                    <ExternalLink className="w-3.5 h-3.5" /> Open as the client sees it
                  </a>
                </div>

                <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
                  <span className="inline-flex items-center gap-1.5 text-slate-600">
                    <Send className="w-4 h-4 text-slate-400" />
                    {s.sent?.at ? when(s.sent.at) : 'Not sent'}
                  </span>
                  <span className={`inline-flex items-center gap-1.5 ${
                    s.email?.confirmed ? 'text-teal-700' : 'text-slate-400'}`}
                    title="An image was fetched. Not proof anyone read it.">
                    <MailOpen className="w-4 h-4" />
                    {!s.email?.tracked
                      ? 'Sent before tracking'
                      : s.email.confirmed
                        ? `Mail opened ${s.email.confirmed}×${
                            s.email.prefetch ? ` (+${s.email.prefetch} on delivery)` : ''}`
                        : s.email.prefetch
                          ? `Delivered — ${s.email.prefetch} scanner fetch${
                              s.email.prefetch === 1 ? '' : 'es'} only`
                          : 'Mail not opened'}
                  </span>
                  <span className={`inline-flex items-center gap-1.5 ${
                    quiet ? 'text-slate-400' : 'text-sky-700'}`}>
                    <Link2 className="w-4 h-4" />
                    {quiet ? 'Never clicked'
                           : `Clicked ${s.shortlist_opens}× · ${ago(s.last_opened_at)}`}
                  </span>
                  <span className={`inline-flex items-center gap-1.5 ${
                    s.verified_numbers?.length ? 'text-emerald-700' : 'text-slate-400'}`}>
                    <ShieldCheck className="w-4 h-4" />
                    {s.verified_numbers?.length
                      ? s.verified_numbers.map(mask).join(', ')
                      : 'Nobody verified'}
                  </span>
                  <span className="inline-flex items-center gap-1.5 text-slate-600">
                    <Eye className="w-4 h-4 text-slate-400" />
                    {s.profiles_opened} profile{s.profiles_opened === 1 ? '' : 's'} opened
                  </span>
                </div>

                <div className="border-t border-slate-100 pt-3 space-y-2">
                  {(s.candidates || []).map((c) => (
                    <div key={c.candidate_id || c.label}
                         className="flex items-center justify-between gap-3 flex-wrap max-md:items-start">
                      <div className="min-w-0">
                        <span className="text-sm font-medium text-slate-800">
                          {c.label}
                        </span>
                        <span className="text-xs text-slate-500 ml-2">
                          {c.opened_count
                            ? `opened ${c.opened_count}× · ${ago(c.last_opened_at)}`
                            : 'never opened'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <DecisionChip decision={c.decision} />
                        {c.decided_by ? (
                          <span className="text-xs text-slate-400">
                            by {mask(c.decided_by)}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <button onClick={() => setOpen((o) => ({ ...o, [s.shortlist_id]: !isOpen }))}
                className="w-full flex items-center justify-center gap-1.5 min-h-[44px]
                           border-t border-slate-100 text-xs font-medium text-slate-500
                           hover:bg-slate-50">
                {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                {isOpen ? 'Hide' : 'Show'} the full trail
                {s.timeline?.length ? ` (${s.timeline.length})` : ''}
              </button>

              {isOpen ? (
                <div className="border-t border-slate-100 bg-slate-50 p-4">
                  {(s.timeline || []).length === 0 ? (
                    <p className="text-sm text-slate-500">
                      Nothing yet. The mail has gone out and the link has not been
                      clicked.
                    </p>
                  ) : (
                    <ol className="space-y-2">
                      {s.timeline.map((e, i) => (
                        <li key={i} className="flex items-start gap-3 text-sm max-md:flex-wrap max-md:gap-x-2 max-md:gap-y-1">
                          <span className="text-xs text-slate-400 tabular-nums w-28 shrink-0 pt-0.5 max-md:w-auto">
                            {when(e.at)}
                          </span>
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full
                                            shrink-0 ${KIND_STYLE[e.kind] || 'bg-slate-100 text-slate-600'}`}>
                            {KIND_LABEL[e.kind] || e.kind}
                          </span>
                          <span className="text-slate-600 min-w-0 max-md:w-full max-md:break-words">
                            {e.candidate_label ? `${e.candidate_label} ` : ''}
                            {e.decision === 'interested' ? '— wants to meet' : ''}
                            {e.decision === 'not_now' ? '— not right' : ''}
                            {e.viewer_mobile ? ` · ${mask(e.viewer_mobile)}` : ''}
                            {e.prefetch ? ' — on delivery, not a read' : ''}
                            {e.provider_error ? ` · ${e.provider_error}` : ''}
                          </span>
                        </li>
                      ))}
                    </ol>
                  )}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}
